import Dexie, { type EntityTable, type Table } from 'dexie';
import type { Category, Household, Membership, MonthlyBudget, NotificationEvent, Operation, PaymentMethod, RecurringOccurrence, RecurringTemplate, Transaction } from '../domain/types';

export interface LocalMeta { key: string; value: unknown }
export interface Draft { key: string; householdId: string; values: unknown; updatedAt: string }
export interface LocalConflict { id: string; householdId: string; entityId: string; operation: Operation; canonical: unknown; createdAt: string }

export class NestDatabase extends Dexie {
  meta!: EntityTable<LocalMeta, 'key'>;
  households!: EntityTable<Household, 'id'>;
  memberships!: Table<Membership, [string, string]>;
  categories!: EntityTable<Category, 'id'>;
  paymentMethods!: EntityTable<PaymentMethod, 'id'>;
  transactions!: EntityTable<Transaction, 'id'>;
  budgets!: EntityTable<MonthlyBudget, 'id'>;
  recurringTemplates!: EntityTable<RecurringTemplate, 'id'>;
  occurrences!: EntityTable<RecurringOccurrence, 'id'>;
  notifications!: EntityTable<NotificationEvent, 'id'>;
  outbox!: EntityTable<Operation, 'opId'>;
  drafts!: EntityTable<Draft, 'key'>;
  conflicts!: EntityTable<LocalConflict, 'id'>;

  constructor(identitySubject: string) {
    super(`our-nest-${identitySubject.replace(/[^a-zA-Z0-9_-]/g, '_')}`);
    this.version(1).stores({
      meta: '&key',
      households: '&id',
      memberships: '&[householdId+profileId], householdId, profileId, status',
      categories: '&id, householdId, [householdId+archived], [householdId+sortOrder]',
      paymentMethods: '&id, householdId, [householdId+archived], [householdId+sortOrder]',
      transactions: '&id, householdId, [householdId+eventDate], [householdId+type], paymentMethodId, paidByProfileId, *allocations.categoryId',
      budgets: '&id, householdId, [householdId+periodStart]',
      occurrences: '&id, householdId, [householdId+dueDate], [householdId+status]',
      notifications: '&id, householdId, [householdId+createdAt]',
      outbox: '&opId, householdId, [householdId+state], predecessorOpId, createdAt',
      drafts: '&key, householdId, updatedAt',
      conflicts: '&id, householdId, entityId, createdAt'
    });
    this.version(2).stores({ recurringTemplates: '&id, householdId, [householdId+active]' });
  }
}

let currentDb: NestDatabase | null = null;

export function getLocalDb(identitySubject: string): NestDatabase {
  const desired = `our-nest-${identitySubject.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  if (currentDb?.name !== desired) {
    currentDb?.close();
    currentDb = new NestDatabase(identitySubject);
  }
  return currentDb;
}

export async function closeLocalDb(): Promise<void> {
  currentDb?.close();
  currentDb = null;
}

export async function persistOperation(db: NestDatabase, operation: Operation, draftKey?: string): Promise<void> {
  await db.transaction('rw', [db.outbox, db.drafts], async () => {
    if (!await db.outbox.get(operation.opId)) {
      const stored = structuredClone(operation);
      if (stored.type.startsWith('transaction.') && stored.expectedVersion !== null) {
        const predecessors = await db.outbox.where('householdId').equals(stored.householdId)
          .filter(op => op.entityId === stored.entityId && ['pending', 'sending', 'acknowledged'].includes(op.state)).sortBy('createdAt');
        const referenced = new Set(predecessors.map(op => op.predecessorOpId));
        const predecessor = predecessors.filter(op => !referenced.has(op.opId)).at(-1);
        if (predecessor) {
          stored.predecessorOpId = predecessor.opId;
          if (predecessor.acknowledgedEntityVersion !== undefined) stored.expectedVersion = predecessor.acknowledgedEntityVersion;
        }
      }
      await db.outbox.add(stored);
    }
    if (draftKey) await db.drafts.delete(draftKey);
  });
}

export async function saveDraft(db: NestDatabase, draft: Draft): Promise<void> {
  await db.drafts.put(draft);
}
